// frontend/app/utils/api.ts

const DEFAULT_API_URL = "http://localhost:8000";

export const getApiUrl = (): string => {
  if (process.env.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL.replace(/\/$/, "");
  }
  // Fallback dinamico in ambiente browser
  if (typeof window !== "undefined") {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    // Se siamo su localhost, assumiamo che il backend sia sulla porta 8000
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return `${protocol}//${hostname}:8000`;
    }
    // Altrimenti proviamo a usare lo stesso host (es. se c'è un reverse proxy che espone tutto sotto lo stesso dominio)
    // O in alternativa il backend standard dell'utente
    return `${protocol}//api.${hostname.replace(/^home\./, "")}`;
  }
  return DEFAULT_API_URL;
};

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

export const apiFetch = async (path: string, options: RequestOptions = {}): Promise<Response> => {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const baseUrl = getApiUrl();
  
  // Rimuove gli slash duplicati ed assicura il prefisso /api
  let cleanPath = path.startsWith("/") ? path : `/${path}`;
  if (!cleanPath.startsWith("/api")) {
    cleanPath = `/api${cleanPath}`;
  }
  let url = `${baseUrl}${cleanPath}`;

  // Gestione query parameters
  if (options.params) {
    const searchParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Prepara gli headers
  const headers = new Headers(options.headers || {});
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const finalOptions: RequestInit = {
    ...options,
    headers,
  };

  const response = await fetch(url, finalOptions);

  if (response.status === 401 && typeof window !== "undefined") {
    // Rimuove il token non valido e reindirizza al login
    localStorage.removeItem("token");
    window.location.href = "/login";
  }

  if (response.status === 403 && typeof window !== "undefined") {
    try {
      const clone = response.clone();
      const errJson = await clone.json();
      if (errJson && errJson.detail === "FORCE_2FA_REQUIRED") {
        if (!window.location.pathname.startsWith("/settings")) {
          window.location.href = "/settings?force2fa=1";
        }
      }
    } catch (_) {}
  }

  return response;
};

export const api = {
  get: (path: string, options: RequestOptions = {}) => apiFetch(path, { ...options, method: "GET" }),
  post: (path: string, body?: any, options: RequestOptions = {}) => {
    const isFormData = body instanceof FormData;
    return apiFetch(path, {
      ...options,
      method: "POST",
      body: isFormData ? body : JSON.stringify(body),
    });
  },
  put: (path: string, body?: any, options: RequestOptions = {}) => {
    const isFormData = body instanceof FormData;
    return apiFetch(path, {
      ...options,
      method: "PUT",
      body: isFormData ? body : JSON.stringify(body),
    });
  },
  delete: (path: string, options: RequestOptions = {}) => apiFetch(path, { ...options, method: "DELETE" }),
};
